import React, { useState, useCallback, useMemo } from 'react';
import {
  CARE_LETTER_VERSION,
  clauses,
  company,
  documentTitle,
  earlyStartDeclaration,
  incompleteClauseNumbers,
  intro,
  isCareLetterComplete,
  signatureFields,
  uploadFields,
} from '../content/careLetter';
import { ACCEPTED_TYPES, formatSize, prepareUpload } from '../utils/prepareUpload';
import './CareLetter.css';

const formatDate = date =>
  date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const normalise = value => value.trim().replace(/\s+/g, ' ').toLowerCase();
const tidy = value => value.trim().replace(/[ \t]+/g, ' ');

// Links open in a new tab so a client part-way through the document does not
// navigate away and lose what they have filled in.
const Part = ({ part }) => {
  const href = part.href || part.link;
  if (!href) return <>{part.text}</>;
  return (
    <a className="cl-link" href={href} target="_blank" rel="noopener noreferrer">
      {part.text}
    </a>
  );
};

const Parts = ({ parts }) =>
  parts.map((part, i) => <Part key={i} part={part} />);

const Block = ({ block }) => {
  switch (block.type) {
    case 'ul':
      return (
        <ul className="cl-list">
          {block.items.map((item, i) => (
            <li key={i}>
              {typeof item === 'string' ? item : <Parts parts={item.parts} />}
            </li>
          ))}
        </ul>
      );
    case 'note':
      return <p className="cl-note">{block.text}</p>;
    case 'address':
      return (
        <address className="cl-address">
          {block.lines.map((line, i) => (
            <span key={i}>{line}</span>
          ))}
        </address>
      );
    default:
      return (
        <p className="cl-p">
          {block.parts ? <Parts parts={block.parts} /> : block.text}
        </p>
      );
  }
};

/**
 * Client Care - Terms of Business.
 *
 * Presentational: owns only the acceptance fields, and hands the completed
 * acceptance up via onAccept. The parent decides what happens next.
 */
const CareLetter = ({ onAccept }) => {
  const [clientNames, setClientNames] = useState('');
  const [signature, setSignature] = useState('');
  const [signatoryName, setSignatoryName] = useState('');
  const [earlyStart, setEarlyStart] = useState(false);
  const [error, setError] = useState('');

  // Identity documents from clause 2. Held as File objects and handed to the
  // parent on accept, so they survive this component unmounting.
  const [files, setFiles] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [preparing, setPreparing] = useState({});

  const handleFileChange = useCallback(async (key, fileList) => {
    const chosen = fileList && fileList[0];
    setFileErrors(prev => ({ ...prev, [key]: '' }));

    if (!chosen) {
      setFiles(prev => ({ ...prev, [key]: null }));
      return;
    }

    setPreparing(prev => ({ ...prev, [key]: true }));
    try {
      const { file, downscaled } = await prepareUpload(chosen);
      setFiles(prev => ({ ...prev, [key]: { file, downscaled } }));
    } catch (err) {
      setFiles(prev => ({ ...prev, [key]: null }));
      setFileErrors(prev => ({ ...prev, [key]: err.message }));
    } finally {
      setPreparing(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  const clearFile = useCallback(key => {
    setFiles(prev => ({ ...prev, [key]: null }));
    setFileErrors(prev => ({ ...prev, [key]: '' }));
    const input = document.getElementById(uploadFields.find(f => f.key === key).id);
    if (input) input.value = '';
  }, []);

  const today = useMemo(() => new Date(), []);
  const dateDisplay = formatDate(today);

  // Terms that still contain placeholder wording must never be accepted by a
  // real client. In development the flow stays clickable so it can be tested.
  const complete = isCareLetterComplete();
  const blockedForDrafting = !complete && process.env.NODE_ENV === 'production';

  const canSubmit =
    clientNames.trim().length > 0 &&
    signature.trim().length > 0 &&
    signatoryName.trim().length > 0 &&
    !blockedForDrafting;

  const handleAccept = useCallback(
    e => {
      e.preventDefault();

      if (blockedForDrafting) return;
      if (!clientNames.trim()) {
        setError('Please enter the full name of every client.');
        return;
      }
      if (!signatoryName.trim()) {
        setError('Please enter the name of the person signing.');
        return;
      }
      if (!signature.trim()) {
        setError('Please sign by typing the name of the person signing.');
        return;
      }
      // The signature is matched against the signatory, not the client list --
      // one person may sign on a document naming several donors.
      if (normalise(signature) !== normalise(signatoryName)) {
        setError('Your signature must match the name of the person signing.');
        return;
      }

      setError('');
      onAccept({
        clientNames: tidy(clientNames),
        signature: tidy(signature),
        signatoryName: tidy(signatoryName),
        date: today.toISOString().slice(0, 10),
        dateDisplay,
        earlyStart,
        version: CARE_LETTER_VERSION,
        files,
      });
    },
    [
      blockedForDrafting,
      clientNames,
      signature,
      signatoryName,
      earlyStart,
      files,
      today,
      dateDisplay,
      onAccept,
    ]
  );

  return (
    <div className="care-letter-page">
      <div className="care-letter-container">
        <header className="cl-header">
          <h1 className="cl-header__title">{documentTitle}</h1>
          <p className="cl-header__company">{company.displayName}</p>
          <p className="cl-header__contact">
            <a href={company.phoneHref}>Telephone/WhatsApp: {company.phone}</a>
            <span className="cl-header__sep" aria-hidden="true">
              ·
            </span>
            <a href={company.emailHref}>{company.email}</a>
          </p>
        </header>

        <section className="cl-intro">
          {intro.map((paragraph, i) => (
            <p key={i} className="cl-p">
              {paragraph}
            </p>
          ))}
        </section>

        {!complete && (
          <div className="cl-draft-notice" role="status">
            <strong>Draft — not ready to send to clients.</strong> Wording is still
            outstanding for clause {incompleteClauseNumbers().join(', ')}. Acceptance is
            blocked in the production build until it is supplied.
          </div>
        )}

        <form className="cl-body" onSubmit={handleAccept} noValidate>
          {clauses.map(clause => (
            <section className="cl-clause" key={clause.n}>
              <div className="cl-clause__header">
                <span className="cl-clause__number">{clause.n}</span>
                <h2 className="cl-clause__title">{clause.title}</h2>
              </div>
              <div className="cl-clause__body">
                {clause.blocks.map((block, i) =>
                  block.type === 'upload' ? (
                    <div className="cl-uploads" key={i}>
                      {uploadFields.map(field => {
                        const chosen = files[field.key];
                        const fieldError = fileErrors[field.key];
                        return (
                          <div className="cl-upload" key={field.key}>
                            <label className="cl-upload__label" htmlFor={field.id}>
                              {field.label}
                            </label>
                            <p className="cl-upload__hint">{field.hint}</p>

                            {chosen ? (
                              <div className="cl-upload__chosen">
                                <span className="cl-upload__file">
                                  {chosen.file.name}{' '}
                                  <span className="cl-upload__size">
                                    ({formatSize(chosen.file.size)}
                                    {chosen.downscaled ? ', resized' : ''})
                                  </span>
                                </span>
                                <button
                                  type="button"
                                  className="cl-upload__remove"
                                  onClick={() => clearFile(field.key)}
                                >
                                  Remove
                                </button>
                              </div>
                            ) : (
                              <input
                                id={field.id}
                                className="cl-upload__input"
                                type="file"
                                accept={ACCEPTED_TYPES}
                                disabled={preparing[field.key]}
                                onChange={e => handleFileChange(field.key, e.target.files)}
                              />
                            )}

                            {preparing[field.key] && (
                              <p className="cl-upload__status">Preparing file…</p>
                            )}
                            {fieldError && (
                              <p className="cl-upload__error" role="alert">
                                {fieldError}
                              </p>
                            )}
                          </div>
                        );
                      })}
                      <p className="cl-upload__note">
                        Optional here — you can also send these later by email or WhatsApp.
                        Maximum 5&nbsp;MB per file; large photographs are resized automatically.
                      </p>
                    </div>
                  ) : (
                    <Block key={i} block={block} />
                  )
                )}
              </div>
            </section>
          ))}

          <section className="cl-sign">
            <div className="cl-early-start">
              <label className="cl-checkbox-label" htmlFor="cl-early-start">
                <input
                  id="cl-early-start"
                  className="cl-checkbox"
                  type="checkbox"
                  checked={earlyStart}
                  onChange={e => setEarlyStart(e.target.checked)}
                />
                <span>{earlyStartDeclaration}</span>
              </label>
            </div>

            <div className="cl-field">
              <label className="cl-label" htmlFor="cl-client-names">
                {signatureFields.clientNamesLabel} <span className="cl-required">*</span>
              </label>
              <textarea
                id="cl-client-names"
                className="cl-textarea"
                rows={2}
                value={clientNames}
                onChange={e => setClientNames(e.target.value)}
                placeholder={signatureFields.clientNamesHint}
                aria-describedby="cl-client-names-help"
              />
              <p id="cl-client-names-help" className="cl-help">
                {signatureFields.clientNamesHint}.
              </p>
            </div>

            <div className="cl-field">
              <label className="cl-label" htmlFor="cl-signatory">
                {signatureFields.signatoryLabel} <span className="cl-required">*</span>
              </label>
              <input
                id="cl-signatory"
                className="cl-input"
                type="text"
                autoComplete="name"
                value={signatoryName}
                onChange={e => setSignatoryName(e.target.value)}
              />
            </div>

            <div className="cl-field">
              <label className="cl-label" htmlFor="cl-signature">
                {signatureFields.signatureLabel} <span className="cl-required">*</span>
              </label>
              <input
                id="cl-signature"
                className="cl-input cl-input--signature"
                type="text"
                autoComplete="off"
                value={signature}
                onChange={e => setSignature(e.target.value)}
                placeholder="Type the name of the person signing"
                aria-describedby="cl-signature-help"
              />
              <p id="cl-signature-help" className="cl-help">
                Typing the name here is your electronic signature. It must match the name
                of the person signing.
              </p>
            </div>

            <div className="cl-field">
              <span className="cl-label">{signatureFields.dateLabel}</span>
              <p className="cl-date">{dateDisplay}</p>
            </div>

            {blockedForDrafting && (
              <div className="cl-error" role="alert">
                These terms are incomplete and cannot be accepted. Please contact us on{' '}
                {company.phone}.
              </div>
            )}

            {error && (
              <div className="cl-error" role="alert">
                {error}
              </div>
            )}

            <button type="submit" className="cl-submit" disabled={!canSubmit}>
              {signatureFields.submitLabel}
            </button>

            <p className="cl-version">Terms version {CARE_LETTER_VERSION}</p>
          </section>
        </form>
      </div>
    </div>
  );
};

export default CareLetter;
